/**
 * AD Hygiene queries - security misconfigurations and policy violations.
 */

import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import type { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

export const AD_HYGIENE_QUERIES: SavedQuery[] = [
  {
    id: 'domains-with-machine-account-quota',
    name: 'Domains with Machine Account Quota > 0',
    type: 'table',
    description:
      'Find domains where any authenticated user can join computers. This enables resource-based constrained delegation attacks.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADDomain'],
        filters: [
          { field: 'machineaccountquota', operator: '>', value: 0, not: false },
        ],
        relationships: [],
      },
      limit: 200,
    },
  },
  {
    id: 'users-password-not-required',
    name: 'Users with Password Not Required',
    type: 'table',
    description:
      'Find user accounts with PASSWD_NOTREQD flag set. These can authenticate without a password.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'passwordnotreqd', operator: '=', value: true, not: false },
        ],
        relationships: [],
      },
      limit: 200,
    },
  },
  {
    id: 'reversible-encryption-passwords',
    name: 'Users with Reversible Encryption Passwords',
    type: 'table',
    description:
      'Find accounts storing passwords with reversible encryption. These passwords can be recovered in plaintext.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [
          {
            field: 'encryptedtextpwdallowed',
            operator: '=',
            value: true,
            not: false,
          },
        ],
        relationships: [],
      },
      limit: 200,
    },
  },
  {
    id: 'tier-zero-non-expiring-passwords',
    name: 'Tier Zero / High Value Users with Non-Expiring Passwords',
    type: 'table',
    description:
      'Find high-value user accounts with passwords set to never expire. These should have regular rotation.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          { field: 'pwdneverexpires', operator: '=', value: true, not: false },
          {
            field: 'tags',
            operator: 'IN',
            value: 'tier-zero',
            reverseOperands: true,
            not: false,
          },
        ],
        relationships: [],
      },
      limit: 100,
    },
  },
  {
    id: 'tier-zero-without-adminsdholder',
    name: 'Tier Zero Principals without AdminSDHolder Protection',
    type: 'table',
    description:
      'Find high-value principals not protected by AdminSDHolder. Their ACLs may be modified by non-privileged users.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADObject'],
        filters: [
          {
            field: 'tags',
            operator: 'IN',
            value: 'tier-zero',
            reverseOperands: true,
            not: false,
          },
          {
            field: 'adminsdholderprotected',
            operator: '=',
            value: false,
            not: false,
          },
        ],
        relationships: [],
      },
      limit: 500,
    },
  },
  {
    id: 'nested-groups-in-tier-zero',
    name: 'Nested Groups within Tier Zero / High Value',
    type: 'graph',
    description:
      'Find groups nested within Tier Zero groups. Nested membership can obscure privilege escalation paths and should be minimized.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADGroup'],
        filters: [
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-512',
            not: true,
          },
          {
            field: 'objectid',
            operator: 'ENDS WITH',
            value: '-519',
            not: true,
          },
        ],
        relationships: [
          {
            label: 'MemberOf',
            optional: false,
            length: 5,
            target: {
              labels: ['ADGroup'],
              filters: [
                {
                  field: 'tags',
                  operator: 'IN',
                  value: 'tier-zero',
                  reverseOperands: true,
                  not: false,
                },
              ],
              relationships: [],
            },
          },
        ],
      },
      limit: 1000,
    },
  },
  {
    id: 'computers-unsupported-os',
    name: 'Computers with End-of-Life Operating Systems',
    type: 'table',
    description:
      'Find computers running end-of-life Windows versions (2000, 2003, 2008, 2012, XP, Vista, 7, 8, ME, NT) that no longer receive security updates.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADComputer'],
        filters: [
          {
            field: '',
            operator: 'OR',
            value: [
              { field: 'operatingsystem', operator: 'CONTAINS', value: '2000' },
              { field: 'operatingsystem', operator: 'CONTAINS', value: '2003' },
              { field: 'operatingsystem', operator: 'CONTAINS', value: '2008' },
              { field: 'operatingsystem', operator: 'CONTAINS', value: '2012' },
              { field: 'operatingsystem', operator: 'CONTAINS', value: 'XP' },
              {
                field: 'operatingsystem',
                operator: 'CONTAINS',
                value: 'Vista',
              },
              {
                field: 'operatingsystem',
                operator: 'CONTAINS',
                value: 'Windows 7',
              },
              {
                field: 'operatingsystem',
                operator: 'CONTAINS',
                value: 'Windows 8',
              },
              { field: 'operatingsystem', operator: 'CONTAINS', value: 'ME' },
              { field: 'operatingsystem', operator: 'CONTAINS', value: 'NT' },
            ],
          },
        ],
        relationships: [],
      },
      limit: 100,
    },
  },
  {
    id: 'domains-smartcard-no-password-expiry',
    name: 'Domains with Smart Card Accounts without Password Expiry',
    type: 'table',
    description:
      'Find domains where smart card-only accounts have passwords that never expire. Smart card passwords should still be rotated.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADDomain'],
        filters: [
          {
            field: 'expirepasswordsonsmartcardonlyaccounts',
            operator: '=',
            value: false,
            not: false,
          },
        ],
        relationships: [],
      },
      limit: 200,
    },
  },
  {
    id: 'tier-zero-users-no-smartcard',
    name: 'Tier Zero / High Value Enabled Users Not Requiring Smart Card',
    type: 'table',
    description:
      'Find Tier Zero users not requiring smart card authentication. Excludes Entra sync accounts.',
    folderId: QueryFolders.AD_ATTACK_PATHS,
    query: {
      node: {
        labels: ['ADUser'],
        filters: [
          {
            field: 'tags',
            operator: 'IN',
            value: 'tier-zero',
            reverseOperands: true,
            not: false,
          },
          {
            field: 'smartcardrequired',
            operator: '=',
            value: false,
            not: false,
          },
        ],
        relationships: [],
      },
      limit: 100,
    },
  },
];
